import { useEffect, useState } from "react";

const DEFAULT_HEIGHT_FACTOR = 0.65;

/**
 * Sets up periodic auto-refresh and a responsive table height.
 *
 * @param {Function} fetchFn     - Called immediately on mount and on each interval tick.
 * @param {number}   intervalMs  - Refresh interval in milliseconds.
 * @param {number}   [heightFactor=0.65] - Fraction of window.innerHeight for max table height.
 * @returns {number} tableMaxHeight - Use in muiTableContainerProps sx.maxHeight.
 *
 * @example
 *   const tableMaxHeight = useTableAutoRefresh(fetchBatchProcessSummary, 3_600_000);
 *   // ...
 *   muiTableContainerProps={{ sx: { maxHeight: tableMaxHeight } }}
 */
export function useTableAutoRefresh(fetchFn, intervalMs, heightFactor = DEFAULT_HEIGHT_FACTOR) {
  const [tableMaxHeight, setTableMaxHeight] = useState(
    window.innerHeight * heightFactor,
  );

  // Initial fetch + periodic auto-refresh
  useEffect(() => {
    fetchFn();
    const id = setInterval(fetchFn, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Responsive table height
  useEffect(() => {
    const handleResize = () =>
      setTableMaxHeight(window.innerHeight * heightFactor);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return tableMaxHeight;
}
